/*  HomeFeatureGallery — unified Trade Republic-style horizontal-scroll
    gallery used across the entire Home page.

    All Home galleries (Funzioni principali, Social, "Cosa puoi fare con
    {dog}", "Scopri ORIO", "Cosa c'è di nuovo") share the same visual
    system so the page reads like a single coherent product.

    Behavior:
      - Tiles are BIG cards (~78% viewport width, aspect 4:5) scrolling
        horizontally with snap-x-mandatory, so ~1.2 cards visible per
        row — TR-style.
      - Each tile fills with a real image (`img` prop) — falls back to
        an ORIO warm gradient if the image is missing/404.
      - Bottom black-fade keeps the title readable on any photo.
      - Optional icon top-left in a glass chip and badge top-right.
      - Whole tile is a link (`to`) or a button (`onClick`).

    Props:
      title, eyebrow, items = [{ id, title, sub, icon, to, onClick,
                                 img?, gradient?, badge? }]
      action?  — optional right-aligned link/action in the header
      testid?  — data-testid for the section wrapper
*/
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function HomeFeatureGallery({ title, eyebrow, items, testid, action }) {
  return (
    <section className="mt-8 -mx-4 sm:-mx-6" data-testid={testid || "home-feature-gallery"}>
      <div className="flex items-end justify-between mb-3 px-4 sm:px-6">
        <div>
          {eyebrow && (
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#B75E2A]">
              {eyebrow}
            </div>
          )}
          <h2
            className="font-display text-[20px] sm:text-[23px] leading-tight tracking-tight text-[#1A1A1A] mt-0.5"
            style={{ fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {title}
          </h2>
        </div>
        {action}
      </div>

      <div
        className="flex gap-3 overflow-x-auto orio-h-scroll pb-2 px-4 sm:px-6 snap-x snap-mandatory"
        style={{ scrollPaddingLeft: "16px", scrollPaddingRight: "16px" }}
      >
        {items.map((it, i) => (
          <Tile key={it.id || i} it={it} index={i} />
        ))}
        {/* trailing spacer so the last card can snap fully into view */}
        <div aria-hidden className="shrink-0 w-1" />
      </div>
    </section>
  );
}

function Tile({ it, index }) {
  const Icon = it.icon;
  const inner = (
    <div className="relative w-full h-full">
      {/* Real image — falls back to the gradient when missing. */}
      {it.img && (
        <img
          src={it.img}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}

      {/* Bottom gradient for text legibility on any background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.25) 66%, rgba(0,0,0,0.70) 100%)",
        }}
      />

      {/* Icon top-left in a glass chip */}
      {Icon && (
        <div className="absolute top-3 left-3">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-white"
            style={{
              background: "rgba(0,0,0,0.30)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.35)",
            }}
          >
            <Icon className="h-4 w-4" strokeWidth={2.2} />
          </div>
        </div>
      )}

      {/* Optional top-right badge */}
      {it.badge && <div className="absolute top-3 right-3">{it.badge}</div>}

      {/* Title + optional chip + sub — all stacked in the bottom text block
          so nothing overlaps with the title itself. */}
      <div className="absolute inset-x-3.5 bottom-3.5">
        {it.chip && (
          <div className="mb-1.5">
            <span
              className="inline-block text-white text-[9.5px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.22)", backdropFilter: "blur(8px)" }}
            >
              {it.chip}
            </span>
          </div>
        )}
        <div
          className="text-white font-display text-[18px] leading-tight"
          style={{ fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.55)" }}
        >
          {it.title}
        </div>
        {it.sub && (
          <div
            className="text-white/90 text-[12px] leading-snug mt-1 line-clamp-2"
            style={{ textShadow: "0 1px 5px rgba(0,0,0,0.6)" }}
          >
            {it.sub}
          </div>
        )}
      </div>
    </div>
  );

  const commonClass =
    "group relative block rounded-3xl overflow-hidden shrink-0 snap-start active:scale-[0.98] transition-transform";
  const commonStyle = {
    width: "min(78vw, 300px)",
    aspectRatio: "4 / 5",
    background: it.gradient || "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
    boxShadow:
      "0 22px 42px -22px rgba(0,0,0,0.32), 0 6px 14px -8px rgba(0,0,0,0.10)",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.24) }}
      className="shrink-0 snap-start"
      style={{ width: "min(78vw, 300px)" }}
    >
      {it.onClick ? (
        <button
          type="button"
          onClick={it.onClick}
          data-testid={`gallery-tile-${it.id}`}
          className={commonClass + " text-left w-full"}
          style={commonStyle}
        >
          {inner}
        </button>
      ) : (
        <Link
          to={it.to}
          data-testid={`gallery-tile-${it.id}`}
          className={commonClass}
          style={commonStyle}
        >
          {inner}
        </Link>
      )}
    </motion.div>
  );
}
